/**
 * ScadaToolbar — Clean minimal toolbar with lucide-react SVG icons.
 * Responsive: wraps into two rows if needed, never overflows.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, Save, Download, Upload,
  Undo2, Redo2,
  Scissors, Copy, Clipboard, CopyPlus, Trash2, CheckSquare,
  MousePointer2, Hand,
  Image, Plus,
  AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  MoveHorizontal, MoveVertical,
  ChevronsUp, ChevronUp, ChevronDown, ChevronsDown,
  RotateCw, FlipHorizontal, FlipVertical,
  Group, Ungroup, Lock, Unlock,
  ZoomIn, ZoomOut, Maximize2,
  Grid3x3, Magnet,
  Settings, LayoutGrid, Paintbrush, Pipette,
} from 'lucide-react';

/* ── colors ── */
const IC = '#7fb8d4'; // default icon color
const IC_H = '#ffffff'; // hover
const IC_A = '#60a5fa'; // active
const IC_D = '#3a5570'; // disabled
const LBL = '#5a8eaa';
const LBL_H = '#ddeeff';
const LBL_A = '#93c5fd';
const BAR = '#0c1420';
const BAR_BD = '#162a40';
const SEP = '#1a3048';

/* ── Icon button (square) ── */
function Btn({ icon: Icon, tip, onClick, active, disabled, danger }) {
  const [h, setH] = useState(false);
  const col = disabled ? IC_D : danger ? '#f87171' : active ? IC_A : h ? IC_H : IC;
  const bg = active ? 'rgba(59,130,246,0.18)' : h && !disabled ? 'rgba(255,255,255,0.07)' : 'transparent';
  const bd = active ? 'rgba(59,130,246,0.5)' : h && !disabled ? 'rgba(255,255,255,0.12)' : 'transparent';
  return (
    <button
      title={tip}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: 28, height: 28, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 4, border: `1px solid ${bd}`, background: bg,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'all .1s', outline: 'none', padding: 0,
      }}
    >
      <Icon size={16} color={col} strokeWidth={1.8} />
    </button>
  );
}

/* ── Labeled button (icon + text) ── */
function LBtn({ icon: Icon, label, tip, onClick, active, danger, green, shortcut }) {
  const [h, setH] = useState(false);
  const iCol = danger ? '#f87171' : green ? '#4ade80' : active ? IC_A : h ? IC_H : IC;
  const lCol = danger ? '#f87171' : green ? '#4ade80' : active ? LBL_A : h ? LBL_H : LBL;
  const bg = active ? 'rgba(59,130,246,0.18)' : h ? 'rgba(255,255,255,0.07)' : 'transparent';
  const bd = active ? 'rgba(59,130,246,0.5)' : h ? 'rgba(255,255,255,0.12)' : 'transparent';
  return (
    <button
      title={shortcut ? `${tip || label} (${shortcut})` : (tip || label)}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        height: 28, padding: '0 7px', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        borderRadius: 4, border: `1px solid ${bd}`, background: bg,
        cursor: 'pointer', transition: 'all .1s', outline: 'none',
      }}
    >
      <Icon size={15} color={iCol} strokeWidth={1.8} />
      <span style={{ fontSize: 10, fontWeight: 700, color: lCol, textTransform: 'uppercase', letterSpacing: '.4px', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

/* ── Tool button (select/pan/pipe etc.) ── */
function TBtn({ icon: Icon, label, active, onClick, shortcut }) {
  const [h, setH] = useState(false);
  const iCol = active ? IC_A : h ? IC_H : IC;
  const lCol = active ? LBL_A : h ? LBL_H : LBL;
  return (
    <button
      title={shortcut ? `${label} (${shortcut})` : label}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        height: 28, padding: '0 7px', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        borderRadius: 4,
        border: `1px solid ${active ? 'rgba(59,130,246,0.5)' : h ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)'}`,
        background: active
          ? 'linear-gradient(135deg,rgba(59,130,246,.25),rgba(37,99,235,.15))'
          : h ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
        cursor: 'pointer', transition: 'all .1s', outline: 'none',
        boxShadow: active ? '0 0 8px rgba(59,130,246,.18)' : 'none',
      }}
    >
      <Icon size={15} color={iCol} strokeWidth={1.8} />
      <span style={{ fontSize: 10, fontWeight: 700, color: lCol, textTransform: 'uppercase', letterSpacing: '.4px', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

const Sep = () => <div style={{ width: 1, height: 18, background: SEP, flexShrink: 0, margin: '0 3px' }} />;

const ZPill = ({ zoom }) => (
  <div style={{
    height: 24, minWidth: 40, padding: '0 6px', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 4, border: `1px solid ${SEP}`,
    background: 'rgba(255,255,255,0.03)',
    fontSize: 10, fontWeight: 800, color: '#7ab8e8',
  }}>
    {Math.round(zoom * 100)}%
  </div>
);

function TitleInput({ value, onChange }) {
  const [f, sf] = useState(false);
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={() => sf(true)}
      onBlur={() => sf(false)}
      style={{
        height: 24, width: 120, padding: '0 8px', flexShrink: 1, minWidth: 50,
        background: f ? 'rgba(30,70,130,.25)' : 'rgba(255,255,255,.03)',
        border: `1px solid ${f ? 'rgba(59,130,246,.50)' : 'rgba(255,255,255,.08)'}`,
        borderRadius: 4, color: '#c8dff0', fontSize: 11, fontWeight: 600,
        outline: 'none', transition: 'all .15s',
      }}
    />
  );
}

const AutoSave = ({ s }) => !s ? null : (
  <span style={{ fontSize: 9, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', color: s === 'saved' ? '#4ade80' : IC }}>
    {s === 'saving' ? '● saving' : '✓ saved'}
  </span>
);

function ImportBtn({ onImport }) {
  const [h, setH] = useState(false);
  return (
    <label
      title="Import JSON"
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        height: 28, padding: '0 7px', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        borderRadius: 4,
        border: `1px solid ${h ? 'rgba(255,255,255,0.12)' : 'transparent'}`,
        background: h ? 'rgba(255,255,255,0.07)' : 'transparent',
        cursor: 'pointer', transition: 'all .1s',
      }}
    >
      <Upload size={15} color={h ? IC_H : IC} strokeWidth={1.8} />
      <span style={{ fontSize: 10, fontWeight: 700, color: h ? LBL_H : LBL, textTransform: 'uppercase', letterSpacing: '.4px' }}>Import</span>
      <input type="file" accept=".json" onChange={onImport} style={{ display: 'none' }} />
    </label>
  );
}

/* ── Canvas resolution presets ── */
const CANVAS_PRESETS = [
  { label: 'Default',      w: 1920, h: 1080, isDefault: true },
  { label: 'Fit to Screen',w: -1,   h: -1   },
  { label: 'Desktop FHD',  w: 1920, h: 1080 },
  { label: 'Desktop HD',   w: 1366, h: 768  },
  { label: '1600 × 900',   w: 1600, h: 900  },
  { label: '1280 × 720',   w: 1280, h: 720  },
  { label: '4K UHD',       w: 3840, h: 2160 },
  { label: 'Tablet Land',  w: 1024, h: 768  },
  { label: 'Tablet Port',  w: 768,  h: 1024 },
  { label: 'Mobile Land',  w: 812,  h: 375  },
  { label: 'Mobile Port',  w: 375,  h: 812  },
  { label: '800 × 600',    w: 800,  h: 600  },
  { label: 'Custom',       w: 0,    h: 0    },
];

function ResolutionSelector({ canvasW, canvasH, onChangeResolution }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customW, setCustomW] = useState(String(canvasW));
  const [customH, setCustomH] = useState(String(canvasH));

  const currentPreset = CANVAS_PRESETS.find(p => !p.isDefault && p.w > 0 && p.w === canvasW && p.h === canvasH);
  const selectValue = currentPreset ? `${currentPreset.w}x${currentPreset.h}` : 'custom';

  const handleSelect = (e) => {
    const val = e.target.value;
    if (val === '0x0') {
      setShowCustom(true);
      setCustomW(String(canvasW));
      setCustomH(String(canvasH));
      return;
    }
    if (val === '-1x-1') {
      // Fit to screen — signal designer to measure actual canvas area
      setShowCustom(false);
      onChangeResolution?.(-1, -1);
      return;
    }
    setShowCustom(false);
    const [w, h] = val.split('x').map(Number);
    onChangeResolution?.(w, h);
  };

  const applyCustom = () => {
    const w = parseInt(customW, 10);
    const h = parseInt(customH, 10);
    if (w > 0 && h > 0) {
      onChangeResolution?.(w, h);
      setShowCustom(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, position: 'relative' }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: IC_D, letterSpacing: '.3px' }}>RES</span>
      <select
        value={selectValue}
        onChange={handleSelect}
        style={{
          height: 22, padding: '0 4px', minWidth: 100,
          background: '#1a2332', border: `1px solid rgba(255,255,255,.15)`,
          borderRadius: 3, color: '#c8dff0', fontSize: 10, fontWeight: 600,
          outline: 'none', cursor: 'pointer',
        }}
      >
        {CANVAS_PRESETS.map(p => (
          <option key={`${p.label}-${p.w}x${p.h}`} value={`${p.w}x${p.h}`} style={{ background: '#1e293b', color: '#e2e8f0', padding: '4px 8px' }}>
            {p.w === 0 ? 'Custom...' : p.w === -1 ? p.label : `${p.label} (${p.w}×${p.h})`}
          </option>
        ))}
        {!currentPreset && selectValue === 'custom' && (
          <option value="custom" style={{ background: '#1e293b', color: '#e2e8f0' }}>{canvasW}×{canvasH}</option>
        )}
      </select>
      {showCustom && (
        <div style={{
          position: 'absolute', top: 26, right: 0, zIndex: 100,
          background: '#0c1420', border: '1px solid #1a3048', borderRadius: 6,
          padding: '8px 10px', display: 'flex', gap: 4, alignItems: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,.5)',
        }}>
          <input value={customW} onChange={e => setCustomW(e.target.value)}
            style={{ width: 48, height: 20, padding: '0 4px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 3, color: '#c8dff0', fontSize: 10, textAlign: 'center', outline: 'none' }}
            placeholder="W"
          />
          <span style={{ color: IC_D, fontSize: 10 }}>×</span>
          <input value={customH} onChange={e => setCustomH(e.target.value)}
            style={{ width: 48, height: 20, padding: '0 4px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 3, color: '#c8dff0', fontSize: 10, textAlign: 'center', outline: 'none' }}
            placeholder="H"
            onKeyDown={e => { if (e.key === 'Enter') applyCustom(); }}
          />
          <button onClick={applyCustom} style={{ height: 20, padding: '0 8px', background: 'rgba(59,130,246,.3)', border: '1px solid rgba(59,130,246,.5)', borderRadius: 3, color: '#93c5fd', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}>OK</button>
        </div>
      )}
    </div>
  );
}

/* ── Numeric field for position/size ── */
function NumField({ label, value, onChange }) {
  const [local, setLocal] = useState(String(Math.round(value)));
  const [f, sf] = useState(false);
  useEffect(() => { if (!f) setLocal(String(Math.round(value))); }, [value, f]);
  const commit = () => {
    const n = parseInt(local, 10);
    if (!isNaN(n) && n !== Math.round(value)) onChange(n);
    else setLocal(String(Math.round(value)));
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: IC_D, textTransform: 'uppercase', letterSpacing: '.3px' }}>{label}</span>
      <input
        value={local}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => sf(true)}
        onBlur={() => { sf(false); commit(); }}
        onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
        style={{
          width: 42, height: 20, padding: '0 4px',
          background: f ? 'rgba(30,70,130,.30)' : 'rgba(255,255,255,.04)',
          border: `1px solid ${f ? 'rgba(59,130,246,.50)' : 'rgba(255,255,255,.08)'}`,
          borderRadius: 3, color: '#c8dff0', fontSize: 10, fontWeight: 600,
          outline: 'none', transition: 'all .12s', textAlign: 'center',
        }}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
export default function ScadaToolbar({
  onClose, onSave, onExport, onImport,
  onUndo, onRedo, canUndo, canRedo,
  onCopy, onCut, onPaste, onDuplicate, onDelete, onSelectAll,
  onRotate, onFlipH, onFlipV,
  onBringToFront, onBringForward, onSendBackward, onSendToBack,
  onAlignLeft, onAlignCenterH, onAlignRight,
  onAlignTop, onAlignCenterV, onAlignBottom,
  onDistributeH, onDistributeV,
  onMatchSize, onApplyProperties, onApplyStyle,
  onGroup, onUngroup, onToggleLock,
  onZoomIn, onZoomOut, onResetView,
  onToggleGrid, onToggleSnap,
  onToggleProps, onToggleSymbols,
  onOpenBackground, onOpenCustomImage,
  selectedTool, setSelectedTool,
  setComponentToAdd, setCustomComponentToAdd, setDrawingLine,
  zoom, showGrid, snapToGrid,
  showPropsPanel, showSymbols,
  isLocked, diagramTitle, setDiagramTitle,
  autoSaveStatus, saved,
  hasSelection, hasMultiSelection,
  selectedComponentData, onUpdateComponentGeometry,
  canvasWidth, canvasHeight, onChangeResolution,
}) {
  const sw = t => {
    setSelectedTool(t);
    setComponentToAdd?.(null);
    setCustomComponentToAdd?.(null);
    setDrawingLine?.(null);
  };

  return (
    <div style={{
      flexShrink: 0, overflow: 'hidden',
      background: BAR, borderBottom: `1px solid ${BAR_BD}`,
      width: '100%', boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: 2, padding: '4px 8px',
        userSelect: 'none',
      }}>

        {/* ── FILE ── */}
        <LBtn icon={ArrowLeft} danger onClick={onClose} tip="Close" />
        <LBtn icon={Save} label="Save" green onClick={onSave} shortcut="Ctrl+S" />

        <Sep />

        {/* ── HISTORY ── */}
        <Btn icon={Undo2} tip="Undo (Ctrl+Z)" onClick={onUndo} disabled={!canUndo} />
        <Btn icon={Redo2} tip="Redo (Ctrl+Y)" onClick={onRedo} disabled={!canRedo} />

        <Sep />

        {/* ── CLIPBOARD ── */}
        <Btn icon={Scissors} tip="Cut (Ctrl+X)" onClick={onCut} />
        <Btn icon={Copy} tip="Copy (Ctrl+C)" onClick={onCopy} />
        <Btn icon={Clipboard} tip="Paste (Ctrl+V)" onClick={onPaste} />
        <Btn icon={CopyPlus} tip="Duplicate (Ctrl+D)" onClick={onDuplicate} />
        <Btn icon={Trash2} tip="Delete (Del)" onClick={onDelete} danger />
        <Btn icon={CheckSquare} tip="Select All (Ctrl+A)" onClick={onSelectAll} />

        <Sep />

        {/* ── ALIGN ── */}
        <Btn icon={AlignStartVertical} tip="Align Left" onClick={onAlignLeft} />
        <Btn icon={AlignCenterVertical} tip="Center Horizontally" onClick={onAlignCenterH} />
        <Btn icon={AlignEndVertical} tip="Align Right" onClick={onAlignRight} />
        <Btn icon={AlignStartHorizontal} tip="Align Top" onClick={onAlignTop} />
        <Btn icon={AlignCenterHorizontal} tip="Center Vertically" onClick={onAlignCenterV} />
        <Btn icon={AlignEndHorizontal} tip="Align Bottom" onClick={onAlignBottom} />
        <Btn icon={MoveHorizontal} tip="Distribute Horizontally" onClick={onDistributeH} />
        <Btn icon={MoveVertical} tip="Distribute Vertically" onClick={onDistributeV} />
        <Btn icon={LayoutGrid} tip="Match Size to First Selected" onClick={onMatchSize} disabled={!hasMultiSelection} />
        <Btn icon={Paintbrush} tip="Apply All Properties from First Selected" onClick={onApplyProperties} disabled={!hasMultiSelection} />
        <Btn icon={Pipette} tip="Apply Style Only (Keep Tags)" onClick={onApplyStyle} disabled={!hasMultiSelection} />

        <Sep />

        {/* ── Z-ORDER ── */}
        <Btn icon={ChevronsUp} tip="Bring to Front" onClick={onBringToFront} />
        <Btn icon={ChevronUp} tip="Bring Forward" onClick={onBringForward} />
        <Btn icon={ChevronDown} tip="Send Backward" onClick={onSendBackward} />
        <Btn icon={ChevronsDown} tip="Send to Back" onClick={onSendToBack} />

        <Sep />

        {/* ── TRANSFORM ── */}
        <Btn icon={RotateCw} tip="Rotate 90°" onClick={onRotate} />
        <Btn icon={FlipHorizontal} tip="Flip Horizontal" onClick={onFlipH} />
        <Btn icon={FlipVertical} tip="Flip Vertical" onClick={onFlipV} />

        <Sep />

        {/* ── GROUP + LOCK ── */}
        <Btn icon={Group} tip="Group" onClick={onGroup} />
        <Btn icon={Ungroup} tip="Ungroup" onClick={onUngroup} />
        <Btn icon={isLocked ? Unlock : Lock} tip={isLocked ? 'Unlock' : 'Lock'} onClick={onToggleLock} active={isLocked} />

        <Sep />

        {/* ── ZOOM ── */}
        <Btn icon={ZoomIn} tip="Zoom In" onClick={onZoomIn} />
        <Btn icon={ZoomOut} tip="Zoom Out" onClick={onZoomOut} />
        <Btn icon={Maximize2} tip="Fit to View" onClick={onResetView} />
        <ZPill zoom={zoom} />

        <Sep />

        {/* ── GRID ── */}
        <Btn icon={Grid3x3} tip="Toggle Grid" onClick={onToggleGrid} active={showGrid} />
        <Btn icon={Magnet} tip="Snap to Grid" onClick={onToggleSnap} active={snapToGrid} />

        <Sep />

        {/* ── TOOLS ── */}
        <TBtn icon={MousePointer2} label="Select" shortcut="V" active={selectedTool === 'select'} onClick={() => sw('select')} />
        <TBtn icon={Hand} label="Pan" shortcut="H" active={selectedTool === 'pan'} onClick={() => sw('pan')} />

        <Sep />

        {/* ── CANVAS ── */}
        <Btn icon={Image} tip="Canvas Background" onClick={onOpenBackground} />
        <Btn icon={Plus} tip="Add Custom Image" onClick={onOpenCustomImage} />

        <Sep />

        {/* ── IMPORT / EXPORT ── */}
        <LBtn icon={Download} label="Export" onClick={onExport} />
        <ImportBtn onImport={onImport} />

        <Sep />

        {/* ── PANELS ── */}
        <Btn icon={Settings} tip="Properties Panel" onClick={onToggleProps} active={showPropsPanel} />
        <Btn icon={LayoutGrid} tip="Symbols Panel" onClick={onToggleSymbols} active={showSymbols} />

        {/* ── RESOLUTION ── */}
        <Sep />
        <ResolutionSelector canvasW={canvasWidth || 1920} canvasH={canvasHeight || 1080} onChangeResolution={onChangeResolution} />

        {/* ── POSITION / SIZE ── */}
        {selectedComponentData && (
          <>
            <Sep />
            <NumField label="X" value={selectedComponentData.x} onChange={v => onUpdateComponentGeometry?.({ x: v })} />
            <NumField label="Y" value={selectedComponentData.y} onChange={v => onUpdateComponentGeometry?.({ y: v })} />
            <NumField label="W" value={selectedComponentData.w} onChange={v => onUpdateComponentGeometry?.({ w: v })} />
            <NumField label="H" value={selectedComponentData.h} onChange={v => onUpdateComponentGeometry?.({ h: v })} />
          </>
        )}

        {/* spacer */}
        <div style={{ flex: 1, minWidth: 8 }} />

        {/* ── RIGHT: autosave + title ── */}
        <AutoSave s={autoSaveStatus} />
        <TitleInput value={diagramTitle} onChange={setDiagramTitle} />
      </div>
    </div>
  );
}

